import Swal from 'sweetalert2';

const VALID_VARIANTS = new Set(['primary','secondary','success','danger','warning','info','light','dark']);

/**
 * Creates default SweetAlert2 options for confirmation dialogs
 * @param {string} text - The text to display
 * @param {string} confirmText - Text for confirm button
 * @param {string} cancelText - Text for cancel button
 * @param {string} variant - Button variant ('primary' by default)
 * @returns {Object} SweetAlert2 options object
 */
function swalDefaults(text, confirmText, cancelText, variant = 'primary') {
  const vv = typeof variant === 'string' ? variant.toLowerCase().trim() : 'primary';
  const v = VALID_VARIANTS.has(vv) ? vv : 'primary';

  return {
    text,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    allowOutsideClick: false,
    allowEscapeKey: true,
    buttonsStyling: false,
    customClass: {
      confirmButton: `btn btn-${v} mx-1`,
      cancelButton: 'btn btn-secondary mx-1'
    },
  };
}

/**
 * Shows a confirmation prompt using SweetAlert2
 * @param {Function} callback - Function to execute if user confirms
 * @param {string} msg - Confirmation message
 * @param {string} variant - Button variant ('primary' by default)
 * @param {string} okTitle - Text for confirm button
 * @param {string} cancelTitle - Text for cancel button
 */
function showConfirmPrompt(callback, msg, variant = 'primary', okTitle, cancelTitle) {
  const t =
    this && typeof this.$t === 'function'
      ? this.$t.bind(this)
      : (s) =>
          ({
            'are-you-sure-you-want-to-proceed': 'Are you sure you want to proceed?',
            'ResearchCreds.yes': 'Yes',
            cancel: 'Cancel',
          }[s] ?? s);
  const text = msg || t('are-you-sure-you-want-to-proceed');
  const confirmText = okTitle || t('ResearchCreds.yes');
  const cancelText = cancelTitle || t('cancel');

  return Swal.fire({
    //title: title || 'Confirm Action',
    //icon: 'info',
    ...swalDefaults(text, confirmText, cancelText, variant),
  }).then((result) => {
    if (result.isConfirmed) {
      callback();
    }
  });
}

/**
 * Shows a confirmation prompt with async/await support
 * @param {string} msg - Confirmation message
 * @param {string} okTitle - Text for confirm button
 * @param {string} cancelTitle - Text for cancel button
 * @param {string} [variant='primary'] - Button variant
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
*/
async function confirmAsync(msg, okTitle, cancelTitle, variant = 'primary') {
  const t =
    this && typeof this.$t === 'function'
      ? this.$t.bind(this)
      : (s) =>
          ({
            'are-you-sure-you-want-to-proceed': 'Are you sure you want to proceed?',
            'ResearchCreds.yes': 'Yes',
            cancel: 'Cancel',
          }[s] ?? s);
  const text = msg || t('are-you-sure-you-want-to-proceed');
  const confirmText = okTitle || t('ResearchCreds.yes');
  const cancelText = cancelTitle || t('cancel');
  const result = await Swal.fire({
    //title: title || 'Confirm Action',
    //icon: 'info',
    ...swalDefaults(text, confirmText, cancelText, variant),
  });
  return result.isConfirmed;
}

const ConfirmationPlugin = {
  installed: false,
  install(Vue) {
    if (this.installed) return;
    this.installed = true;
    Vue.mixin({
      methods: {
        showConfirmPrompt,
        confirmAsync,
      },
    });
  },
};

export { showConfirmPrompt, confirmAsync };
export default ConfirmationPlugin;
